<template>
  <section
    :id="`${cms.scroll_id}`"
    v-editable="cms"
    :class="[name]"
    :style="mainCssVars"
  >
    <grid
      :class="['grid--container', `${name}__grid`]"
      :layout="[
        {
          size: 1,
          columns: '1fr',
          gap: '6px',
        },
        {
          size: 2,
          columns: '1fr',
          gap: '10px',
        },
      ]"
    >
      <div v-if="loadError">{{ cms.copy_no_results }}</div>
      <grid-item
        v-else
        :class="`${name}__grid-item`"
        :layout="[
          { size: 1, x: 1, width: 12 },
          { size: 3, x: 1, width: 24 },
        ]"
      >
        <div v-for="department in jobDepartments" :key="department.id">
          <a-heading
            :class="`${name}__heading`"
            :cms="{ heading: department.name }"
            html-tag="h3"
          />
          <div
            v-for="job in department.jobs"
            :key="job.id"
            :class="`${name}__job`"
            :data-id="job.id"
          >
            <a-link-v2 :url="getJobURL(job.id)" :class="[`${name}__link`]">
              <div :class="`${name}__job-select`">
                <i :class="[`${name}__icon`, `${name}__icon-before`, icon]"></i>
              </div>
              <a-copy
                :cms="{ heading: job.title }"
                :class="`${name}__job-entry`"
              />
            </a-link-v2>
          </div>
        </div>
      </grid-item>
    </grid>
  </section>
</template>

<script>
import data from '~/components/organisms/o-list-jobs/o-list-jobs-v1.data'
import Colors from '~/mixins/colors'
import Jobs from '~/mixins/jobs'

export default {
  mixins: [Colors, Jobs],
  props: {
    cms: {
      type: Object,
      default() {
        const config = {}
        return { ...data, ...config }
      },
    },
  },

  data() {
    return {
      name: 'o-list-job-v1',
      jobDepartments: null,
      loadError: false,
    }
  },

  computed: {
    mainCssVars() {
      return {
        '--bg-color': this.setColor(
          (this.cms.color_background || {}).color || 'white',
          this.cms.color_gradient
        ),
        '--icon-color': this.setColor(
          (this.cms.color_background || {}).color || '#000000'
        ),
        '--text-color': this.setColor(
          (this.cms.color_text || {}).color || 'black'
        ),
        '--hover-color': this.setColor(
          (this.cms.color_hover || {}).color || '#FF3476'
        ),
        '--disabled-color': this.setColor(
          (this.cms.color_disabled || {}).color || '#888b8d'
        ),
        '--spacing-top': this.cms.spacing_top || '100px',
        '--spacing-bottom': this.cms.spacing_bottom || '100px',
      }
    },
    icon() {
      return this.cms.icon ? this.cms.icon : 'icon-arrow-right'
    },
  },

  async mounted() {
    try {
      const greenhouseData = await this.fetchJobs()

      if (!greenhouseData) {
        this.loadError = true
        return
      }
      // Filter jobs by dept
      this.jobDepartments = greenhouseData.departments.filter((department) => {
        // Only return active job departments
        return department.jobs.length
      })
    } catch (error) {
      this.loadError = true
    }
  },

  methods: {
    getJobURL(jobId) {
      const route = 'en-gb/join-us/job-detail?id=' + jobId

      return route
    },
  },
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.o-list-job-v1 {
  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    border 0.2s ease-in-out;
  background: var(--bg-color);
  border-color: var(--text-color);
  color: var(--text-color);
  padding: var(--spacing-top) 0 var(--spacing-bottom);

  ::v-deep a,
  ::v-deep .a-cta {
    color: var(--text-color);
    &:hover,
    &:active {
      color: var(--hover-color);
    }
    &:disabled,
    &.disabled {
      color: var(--disabled-color);
    }
  }

  &__heading {
    @include font-size(16, 28px);

    margin: 40px 0 0;
    color: #ff3476;
  }

  &__header {
    ::v-deep .m-section-header-v2__heading {
      @include font-size(65, 78px);
    }
  }
  &__job {
    padding: 40px 0;
  }
  &__link {
    display: flex;
    align-items: center;

    &:hover {
      .o-list-job-v1__job-select {
        display: block;
        background-color: var(--hover-color);
        color: var(--icon-color);
      }
      ::v-deep .a-copy {
        color: var(--hover-color);
      }
    }
  }
  &__job-select {
    display: none;
    transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
      border 0.2s ease-in-out;
    width: 46px;
    height: 46px;
    border-radius: 27px;
    background-color: var(--text-color);
    color: var(--bg-color);
    align-self: center;
    margin-right: 23px;
  }
  &__job-entry {
    ::v-deep .a-copy__eyebrow {
      @include font-size(16, 28px);

      margin-bottom: 10px;
    }
    ::v-deep .a-copy__heading {
      margin-bottom: 0;
    }
  }
  &__icon {
    @include font-size(20, 46px);

    padding: 0 13px;
  }
}

@include media-breakpoint-up(lg) {
  .o-list-job-v1 {
    &__header {
      ::v-deep .m-section-header-v2__heading {
        @include font-size(83, 96px);
      }
    }
    &__job-select {
      width: 54px;
      height: 54px;
      border-radius: 27px;
    }
    &__icon {
      @include font-size(20, 54px);

      padding: 0 17px;
    }
    &__job-entry {
      ::v-deep .a-copy__eyebrow {
        @include font-size(18, 25.2px);
      }
      ::v-deep .a-copy__heading {
        @include font-size(36, 40.8px);
      }
    }
  }
}
</style>
